import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import {
  stockPortfolioService,
  StockHolding,
  StockTransaction,
  StockCompany,
  StockDividend,
  PortfolioSummary,
  TransactionFormData,
  CompanyFormData,
  DividendFormData,
  CashTransaction,
  CashFormData
} from '../../services/stockPortfolioService';
import './StockPortfolio.css';

interface StockPortfolioProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const StockPortfolio: React.FC<StockPortfolioProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'portfolio' | 'transactions' | 'companies' | 'dividends' | 'add' | 'cash'>('portfolio');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [holdings, setHoldings] = useState<StockHolding[]>([]);
  const [portfolioSummary, setPortfolioSummary] = useState<PortfolioSummary | null>(null);
  const [transactions, setTransactions] = useState<StockTransaction[]>([]);
  const [companies, setCompanies] = useState<StockCompany[]>([]);
  const [dividends, setDividends] = useState<StockDividend[]>([]);
  const [cashBalance, setCashBalance] = useState<number>(0);
  const [cashTransactions, setCashTransactions] = useState<CashTransaction[]>([]);

  // UI states for portfolio tab
  const [summaryExpanded, setSummaryExpanded] = useState(true);
  const [chartExpanded, setChartExpanded] = useState(true);
  const [chartMetric, setChartMetric] = useState<'current_value' | 'total_cost'>('current_value');
  const [chartView, setChartView] = useState<'stock' | 'industry' | 'strategy'>('stock');
  const [portfolioSearchQuery, setPortfolioSearchQuery] = useState('');
  const [filteredHoldings, setFilteredHoldings] = useState<StockHolding[]>([]);

  // Form states for Add tab
  const [addType, setAddType] = useState<'transaction' | 'company' | 'dividend'>('transaction');
  const [newTransaction, setNewTransaction] = useState<TransactionFormData>({
    stock_code: '',
    transaction_type: 'buy',
    quantity: 0,
    price_per_share: 0,
    transaction_date: stockPortfolioService.getCurrentDate(),
    notes: ''
  });
  const [newCashEntry, setNewCashEntry] = useState<CashFormData>({
    amount: 0,
    transaction_date: stockPortfolioService.getCurrentDate(),
    notes: ''
  });
  const [newCompany, setNewCompany] = useState<CompanyFormData>({
    stock_code: '',
    company_name: ''
  });
  const [newDividend, setNewDividend] = useState<DividendFormData>({
    stock_code: '',
    dividend_type: 'cash',
    payment_date: stockPortfolioService.getCurrentDate(),
    shares_held: undefined,
    notes: ''
  });

  // Autocomplete states
  const [transactionSearchQuery, setTransactionSearchQuery] = useState('');
  const [dividendSearchQuery, setDividendSearchQuery] = useState('');
  const [showTransactionDropdown, setShowTransactionDropdown] = useState(false);
  const [showDividendDropdown, setShowDividendDropdown] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user, activeTab]);

  // Filter holdings based on search query
  useEffect(() => {
    if (portfolioSearchQuery.trim() === '') {
      setFilteredHoldings(holdings);
    } else {
      const lowerQuery = portfolioSearchQuery.toLowerCase();
      const filtered = holdings.filter(holding =>
        holding.stock_code.toLowerCase().includes(lowerQuery) ||
        (holding.company_name && holding.company_name.toLowerCase().includes(lowerQuery))
      );
      setFilteredHoldings(filtered);
    }
  }, [holdings, portfolioSearchQuery]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.sp-autocomplete-container')) {
        setShowTransactionDropdown(false);
        setShowDividendDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'portfolio') {
        await Promise.all([
          loadHoldings(),
          loadPortfolioSummary()
        ]);
      } else if (activeTab === 'transactions') {
        await loadTransactions();
      } else if (activeTab === 'companies') {
        await loadCompanies();
      } else if (activeTab === 'dividends') {
        await loadDividends();
      } else if (activeTab === 'add') {
        await loadCompanies();
      } else if (activeTab === 'cash') {
        await Promise.all([
          loadCashBalance(),
          loadCashTransactions()
        ]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadHoldings = async () => {
    if (!user?.id) return;
    const response = await stockPortfolioService.getUserHoldings(user.id.toString());
    if (response.success && response.holdings) {
      setHoldings(response.holdings);
    }
  };

  const loadPortfolioSummary = async () => {
    if (!user?.id) return;
    const response = await stockPortfolioService.getPortfolioValue(user.id.toString());
    if (response.success && response.portfolio) {
      setPortfolioSummary(response.portfolio);
    }
  };

  const loadTransactions = async () => {
    if (!user?.id) return;
    const response = await stockPortfolioService.getUserTransactions(user.id.toString(), undefined, 100);
    if (response.success && response.transactions) {
      setTransactions(response.transactions);
    }
  };

  const loadCompanies = async () => {
    const response = await stockPortfolioService.getCompanies();
    if (response.success && response.companies) {
      setCompanies(response.companies);
    }
  };

  const loadDividends = async () => {
    if (!user?.id) return;
    const response = await stockPortfolioService.getUserDividends(user.id.toString(), undefined, undefined, 100);
    if (response.success && response.dividends) {
      setDividends(response.dividends);
    }
  };

  const loadCashBalance = async () => {
    if (!user?.id) return;
    const response = await stockPortfolioService.getCashBalance(user.id.toString());
    if (response.success) {
      setCashBalance(response.balance);
    }
  };

  const loadCashTransactions = async () => {
    if (!user?.id) return;
    const response = await stockPortfolioService.getCashTransactions(user.id.toString(), 100);
    if (response.success && response.transactions) {
      setCashTransactions(response.transactions);
    }
  };

  // Filter companies based on search query
  const getFilteredCompanies = (query: string) => {
    if (!query.trim()) return companies;
    const lowerQuery = query.toLowerCase();
    return companies.filter(company =>
      company.stock_code.toLowerCase().includes(lowerQuery) ||
      company.company_name.toLowerCase().includes(lowerQuery)
    );
  };

  // Handle transaction stock selection
  const handleTransactionStockSelect = (stockCode: string, companyName: string) => {
    setNewTransaction({ ...newTransaction, stock_code: stockCode });
    setTransactionSearchQuery(`${stockCode} - ${companyName}`);
    setShowTransactionDropdown(false);
  };

  // Handle dividend stock selection
  const handleDividendStockSelect = (stockCode: string, companyName: string) => {
    setNewDividend({ ...newDividend, stock_code: stockCode });
    setDividendSearchQuery(`${stockCode} - ${companyName}`);
    setShowDividendDropdown(false);
  };

  const handleAddTransaction = async () => {
    if (!user?.id || !newTransaction.stock_code.trim() || newTransaction.quantity <= 0 || newTransaction.price_per_share <= 0) {
      return;
    }

    try {
      const validation = stockPortfolioService.validateTransactionData(newTransaction);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const response = await stockPortfolioService.createTransaction(user.id.toString(), newTransaction);

      if (response.success) {
        // Automatically create/update price point for the transaction date
        try {
          await stockPortfolioService.updateStockPrice({
            stock_code: newTransaction.stock_code,
            price: newTransaction.price_per_share,
            price_date: newTransaction.transaction_date
          });
        } catch (priceError) {
          // Log error but don't fail the transaction creation
          console.error('Error creating price point:', priceError);
        }

        setNewTransaction({
          stock_code: '',
          transaction_type: 'buy',
          quantity: 0,
          price_per_share: 0,
          transaction_date: stockPortfolioService.getCurrentDate(),
          notes: ''
        });
        setTransactionSearchQuery('');
        setShowTransactionDropdown(false);
        setActiveTab('portfolio');
        await loadData();
      } else {
        setError(response.message || 'Failed to create transaction');
      }
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError('Failed to create transaction');
    }
  };

  const handleAddCompany = async () => {
    if (!newCompany.stock_code.trim() || !newCompany.company_name.trim()) {
      return;
    }

    try {
      const validation = stockPortfolioService.validateCompanyData(newCompany);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const response = await stockPortfolioService.createCompany(newCompany);

      if (response.success) {
        setNewCompany({
          stock_code: '',
          company_name: ''
        });
        setAddType('transaction');
        await loadCompanies();
      } else {
        setError(response.message || 'Failed to create company');
      }
    } catch (err) {
      console.error('Error creating company:', err);
      setError('Failed to create company');
    }
  };

  const handleAddDividend = async () => {
    if (!user?.id || !newDividend.stock_code.trim()) {
      return;
    }

    try {
      const validation = stockPortfolioService.validateDividendData(newDividend);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const response = await stockPortfolioService.createDividend(user.id.toString(), newDividend);

      if (response.success) {
        setNewDividend({
          stock_code: '',
          dividend_type: 'cash',
          payment_date: stockPortfolioService.getCurrentDate(),
          shares_held: undefined,
          notes: ''
        });
        setDividendSearchQuery('');
        setShowDividendDropdown(false);
        setActiveTab('dividends');
        await loadData();
      } else {
        setError(response.message || 'Failed to create dividend');
      }
    } catch (err) {
      console.error('Error creating dividend:', err);
      setError('Failed to create dividend');
    }
  };

  const handleDeleteTransaction = async (transactionId: number) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;

    try {
      const response = await stockPortfolioService.deleteTransaction(transactionId.toString());

      if (response.success) {
        await loadData();
      } else {
        setError(response.message || 'Failed to delete transaction');
      }
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction');
    }
  };

  const handleDeleteDividend = async (dividendId: number) => {
    if (!window.confirm('Are you sure you want to delete this dividend?')) return;

    try {
      const response = await stockPortfolioService.deleteDividend(dividendId.toString());

      if (response.success) {
        await loadData();
      } else {
        setError(response.message || 'Failed to delete dividend');
      }
    } catch (err) {
      console.error('Error deleting dividend:', err);
      setError('Failed to delete dividend');
    }
  };

  const handleAddCash = async (type: 'topup' | 'deduct') => {
    if (!user?.id || newCashEntry.amount <= 0) {
      return;
    }

    try {
      const validation = stockPortfolioService.validateCashData(newCashEntry);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const response = type === 'topup'
        ? await stockPortfolioService.topupCash(user.id.toString(), newCashEntry)
        : await stockPortfolioService.deductCash(user.id.toString(), newCashEntry);

      if (response.success) {
        setNewCashEntry({
          amount: 0,
          transaction_date: stockPortfolioService.getCurrentDate(),
          notes: ''
        });
        await loadData();
      } else {
        setError(response.message || `Failed to ${type} cash`);
      }
    } catch (err) {
      console.error(`Error in ${type} cash:`, err);
      setError(`Failed to ${type} cash`);
    }
  };

  // Type for chart data entries
  type ChartDataEntry = {
    name: string;
    value: number;
    percent: number;
    company_name: string;
    stocks?: string[];
    count?: number;
    gainLossPercent: number;
    currentValue: number;
  };

  // Calculate chart data based on selected metric and view type
  const chartData = useMemo<ChartDataEntry[]>(() => {
    if (!holdings || holdings.length === 0) return [];

    // Filter holdings with positive values for selected metric
    const validHoldings = holdings.filter(holding => {
      const value = chartMetric === 'current_value'
        ? (holding.current_value ?? 0)
        : (holding.total_cost ?? 0);
      return value > 0 && !isNaN(value) && isFinite(value);
    });

    if (validHoldings.length === 0) return [];

    // Calculate total sum
    const totalSum = validHoldings.reduce((sum, holding) => {
      const value = chartMetric === 'current_value'
        ? (holding.current_value ?? 0)
        : (holding.total_cost ?? 0);
      return sum + (isNaN(value) || !isFinite(value) ? 0 : value);
    }, 0);

    if (totalSum === 0 || isNaN(totalSum) || !isFinite(totalSum)) return [];

    if (chartView === 'strategy') {
      // Group by strategy
      const strategyMap = new Map<string, {
        value: number;
        count: number;
        stocks: string[];
        totalCurrentValue: number;
        totalCost: number;
      }>();

      validHoldings.forEach(holding => {
        const strategy = holding.strategy
          ? (holding.strategy === 'dividend' ? 'Dividend' : 'Growth')
          : 'Unknown Strategy';
        const value = chartMetric === 'current_value'
          ? (holding.current_value ?? 0)
          : (holding.total_cost ?? 0);
        const safeValue = isNaN(value) || !isFinite(value) ? 0 : value;
        const currentValue = holding.current_value ?? 0;
        const cost = holding.total_cost ?? 0;

        if (strategyMap.has(strategy)) {
          const existing = strategyMap.get(strategy)!;
          existing.value += safeValue;
          existing.count += 1;
          existing.stocks.push(holding.stock_code);
          existing.totalCurrentValue += currentValue;
          existing.totalCost += cost;
        } else {
          strategyMap.set(strategy, {
            value: safeValue,
            count: 1,
            stocks: [holding.stock_code],
            totalCurrentValue: currentValue,
            totalCost: cost
          });
        }
      });

      // Convert to array and calculate percentages
      const data = Array.from(strategyMap.entries()).map(([strategy, data]) => {
        const percent = totalSum > 0 ? (data.value / totalSum) * 100 : 0;
        // Calculate gain/loss percentage for strategy
        const gainLossPercent = data.totalCost > 0
          ? ((data.totalCurrentValue - data.totalCost) / data.totalCost) * 100
          : 0;
        return {
          name: strategy,
          value: data.value,
          percent: isNaN(percent) || !isFinite(percent) ? 0 : percent,
          company_name: `${data.count} ${data.count === 1 ? 'stock' : 'stocks'}`,
          count: data.count,
          stocks: data.stocks,
          gainLossPercent: isNaN(gainLossPercent) || !isFinite(gainLossPercent) ? 0 : gainLossPercent,
          currentValue: data.totalCurrentValue
        };
      });

      // Sort by value (largest to smallest)
      return data.sort((a, b) => b.value - a.value);
    } else if (chartView === 'industry') {
      // Group by industry
      const industryMap = new Map<string, {
        value: number;
        count: number;
        stocks: string[];
        totalCurrentValue: number;
        totalCost: number;
      }>();

      validHoldings.forEach(holding => {
        const industry = holding.industry || 'Unknown Industry';
        const value = chartMetric === 'current_value'
          ? (holding.current_value ?? 0)
          : (holding.total_cost ?? 0);
        const safeValue = isNaN(value) || !isFinite(value) ? 0 : value;
        const currentValue = holding.current_value ?? 0;
        const cost = holding.total_cost ?? 0;

        if (industryMap.has(industry)) {
          const existing = industryMap.get(industry)!;
          existing.value += safeValue;
          existing.count += 1;
          existing.stocks.push(holding.stock_code);
          existing.totalCurrentValue += currentValue;
          existing.totalCost += cost;
        } else {
          industryMap.set(industry, {
            value: safeValue,
            count: 1,
            stocks: [holding.stock_code],
            totalCurrentValue: currentValue,
            totalCost: cost
          });
        }
      });

      // Convert to array and calculate percentages
      const data = Array.from(industryMap.entries()).map(([industry, data]) => {
        const percent = totalSum > 0 ? (data.value / totalSum) * 100 : 0;
        // Calculate gain/loss percentage for industry
        const gainLossPercent = data.totalCost > 0
          ? ((data.totalCurrentValue - data.totalCost) / data.totalCost) * 100
          : 0;
        return {
          name: industry,
          value: data.value,
          percent: isNaN(percent) || !isFinite(percent) ? 0 : percent,
          company_name: `${data.count} ${data.count === 1 ? 'stock' : 'stocks'}`,
          count: data.count,
          stocks: data.stocks,
          gainLossPercent: isNaN(gainLossPercent) || !isFinite(gainLossPercent) ? 0 : gainLossPercent,
          currentValue: data.totalCurrentValue
        };
      });

      // Sort by value (largest to smallest)
      return data.sort((a, b) => b.value - a.value);
    } else {
      // By stock (original logic)
      const data = validHoldings.map(holding => {
        const value = chartMetric === 'current_value'
          ? (holding.current_value ?? 0)
          : (holding.total_cost ?? 0);
        const safeValue = isNaN(value) || !isFinite(value) ? 0 : value;
        const percent = totalSum > 0 ? (safeValue / totalSum) * 100 : 0;
        // Calculate gain/loss percentage for stock
        const gainLossPercent = holding.total_cost > 0
          ? ((holding.current_value - holding.total_cost) / holding.total_cost) * 100
          : (holding.gain_loss_percent ?? 0);
        return {
          name: holding.stock_code,
          value: safeValue,
          percent: isNaN(percent) || !isFinite(percent) ? 0 : percent,
          company_name: holding.company_name || 'Unknown Company',
          gainLossPercent: isNaN(gainLossPercent) || !isFinite(gainLossPercent) ? 0 : gainLossPercent,
          currentValue: holding.current_value ?? 0
        };
      });

      // Sort by value (largest to smallest)
      return data.sort((a, b) => b.value - a.value);
    }
  }, [holdings, chartMetric, chartView]);


  const renderPortfolioTab = () => {
    if (loading) {
      return (
        <div className="sp-loading-state">
          <div className="sp-loading-spinner"></div>
          <p>Loading portfolio...</p>
        </div>
      );
    }

    if (!portfolioSummary || holdings.length === 0) {
      return (
        <div className="sp-empty-state">
          <p>No holdings found</p>
          <button onClick={() => setActiveTab('add')} className="sp-add-first-btn">
            Add your first transaction
          </button>
        </div>
      );
    }

    return (
      <div className="sp-portfolio-content">
        {/* Portfolio Summary - Collapsible Section */}
        <div className="sp-summary-section">
          <button
            className="sp-summary-toggle"
            onClick={() => setSummaryExpanded(!summaryExpanded)}
          >
            <span className="sp-toggle-text">/portfolio-summary</span>
            <span className="sp-toggle-arrow">{summaryExpanded ? '▼' : '▶'}</span>
          </button>

          <div className={`sp-summary-content ${summaryExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="sp-summary-cards">
              <div className="sp-summary-card">
                <div className="sp-summary-label">Total Cost</div>
                <div className="sp-summary-value">{stockPortfolioService.formatCurrency(portfolioSummary.total_cost)}</div>
              </div>

              <div className="sp-summary-card">
                <div className="sp-summary-label">Current Value</div>
                <div className="sp-summary-value">{stockPortfolioService.formatCurrency(portfolioSummary.total_current_value)}</div>
              </div>

              <div className={`sp-summary-card ${portfolioSummary.unrealized_gain_loss >= 0 ? 'positive' : 'negative'}`}>
                <div className="sp-summary-label">Unrealized Gain/Loss</div>
                <div className="sp-summary-value">
                  {stockPortfolioService.formatCurrency(portfolioSummary.unrealized_gain_loss)}
                  <span className="sp-summary-percent">
                    ({stockPortfolioService.formatPercentage(portfolioSummary.unrealized_gain_loss_percent)})
                  </span>
                </div>
              </div>

              <div className={`sp-summary-card ${portfolioSummary.realized_gain_loss >= 0 ? 'positive' : 'negative'}`}>
                <div className="sp-summary-label">Realized Gain/Loss</div>
                <div className="sp-summary-value">
                  {stockPortfolioService.formatCurrency(portfolioSummary.realized_gain_loss)}
                </div>
              </div>

              <div className="sp-summary-card">
                <div className="sp-summary-label">Cash Dividends</div>
                <div className="sp-summary-value">{stockPortfolioService.formatCurrency(portfolioSummary.total_cash_dividends_received)}</div>
              </div>

              <div className="sp-summary-card">
                <div className="sp-summary-label">Cash Balance</div>
                <div className="sp-summary-value">{stockPortfolioService.formatCurrency(cashBalance)}</div>
              </div>

              <div className="sp-summary-card highlighted">
                <div className="sp-summary-label">Total Portfolio</div>
                <div className="sp-summary-value">
                  {stockPortfolioService.formatCurrency(portfolioSummary.total_current_value + cashBalance)}
                </div>
              </div>

              <div className="sp-summary-card">
                <div className="sp-summary-label">Cash/Stock Ratio</div>
                <div className="sp-summary-value">
                  {((cashBalance / (portfolioSummary.total_current_value + cashBalance || 1)) * 100).toFixed(1)}% /
                  {((portfolioSummary.total_current_value / (portfolioSummary.total_current_value + cashBalance || 1)) * 100).toFixed(1)}%
                </div>
              </div>

              <div className={`sp-summary-card ${portfolioSummary.total_return >= 0 ? 'positive' : 'negative'}`}>
                <div className="sp-summary-label">Total Return</div>
                <div className="sp-summary-value">
                  {stockPortfolioService.formatCurrency(portfolioSummary.total_return)}
                  <span className="sp-summary-percent">
                    ({stockPortfolioService.formatPercentage(portfolioSummary.total_return_percent)})
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Portfolio Allocation Chart - Collapsible Section */}
        {chartData.length > 0 && (
          <div className="sp-chart-section">
            <button
              className="sp-chart-toggle"
              onClick={() => setChartExpanded(!chartExpanded)}
            >
              <span className="sp-toggle-text">/portfolio-allocation</span>
              <span className="sp-toggle-arrow">{chartExpanded ? '▼' : '▶'}</span>
            </button>

            <div className={`sp-chart-content ${chartExpanded ? 'expanded' : 'collapsed'}`}>
              {/* View Toggle (Stock vs Industry vs Strategy) */}
              <div className="sp-chart-view-toggle">
                <button
                  className={`sp-view-btn ${chartView === 'stock' ? 'active' : ''}`}
                  onClick={() => setChartView('stock')}
                >
                  By Stock
                </button>
                <button
                  className={`sp-view-btn ${chartView === 'industry' ? 'active' : ''}`}
                  onClick={() => setChartView('industry')}
                >
                  By Industry
                </button>
                <button
                  className={`sp-view-btn ${chartView === 'strategy' ? 'active' : ''}`}
                  onClick={() => setChartView('strategy')}
                >
                  By Strategy
                </button>
              </div>

              {/* Metric Toggle */}
              <div className="sp-chart-metric-toggle">
                <button
                  className={`sp-metric-btn ${chartMetric === 'current_value' ? 'active' : ''}`}
                  onClick={() => setChartMetric('current_value')}
                >
                  Current Value
                </button>
                <button
                  className={`sp-metric-btn ${chartMetric === 'total_cost' ? 'active' : ''}`}
                  onClick={() => setChartMetric('total_cost')}
                >
                  Total Cost
                </button>
              </div>

              {/* Allocation List */}
              <div className="sp-allocation-list">
                {chartData.map((entry, index) => {
                  // Always show current value and gain/loss
                  return (
                    <div
                      key={`allocation-${index}`}
                      className="sp-allocation-item"
                      onClick={() => {
                        if (chartView === 'stock') {
                          navigate(`/finance/stocks/company/${entry.name}`);
                        }
                      }}
                      style={{ cursor: chartView === 'stock' ? 'pointer' : 'default' }}
                    >
                      <div className="sp-allocation-info">
                        <span className="sp-allocation-name">{entry.name}</span>
                        {(chartView === 'industry' || chartView === 'strategy') && entry.stocks && (
                          <span className="sp-allocation-stocks">{entry.stocks.join(', ')}</span>
                        )}
                        <div className="sp-allocation-value-row">
                          <span className="sp-allocation-value">
                            {stockPortfolioService.formatCurrency(entry.currentValue)}
                          </span>
                          <span className={`sp-allocation-gain ${entry.gainLossPercent >= 0 ? 'positive' : 'negative'}`}>
                            {entry.gainLossPercent >= 0 ? '+' : ''}{entry.gainLossPercent.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <span className="sp-allocation-percent">{entry.percent.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Search Field */}
        <div className="sp-portfolio-search">
          <input
            type="text"
            placeholder="Search holdings..."
            value={portfolioSearchQuery}
            onChange={(e) => setPortfolioSearchQuery(e.target.value)}
            className="sp-portfolio-search-input"
          />
        </div>

        {/* Holdings List */}
        {filteredHoldings.length === 0 ? (
          <div className="sp-empty-state">
            <p>No holdings found matching "{portfolioSearchQuery}"</p>
            <button onClick={() => setPortfolioSearchQuery('')} className="sp-add-first-btn">
              Clear search
            </button>
          </div>
        ) : (
          <div className="sp-holdings-list">
            {filteredHoldings.map((holding) => (
              <div
                key={holding.stock_code}
                className="sp-holding-item"
                onClick={() => navigate(`/finance/stocks/company/${holding.stock_code}`)}
              >
                <div className="sp-holding-header">
                  <div className="sp-holding-code">{holding.stock_code}</div>
                  <div className={`sp-holding-gain ${(holding.total_return_percent ?? holding.gain_loss_percent) >= 0 ? 'positive' : 'negative'}`}>
                    {stockPortfolioService.formatPercentage(holding.total_return_percent ?? holding.gain_loss_percent)}
                  </div>
                </div>
                <div className="sp-holding-company">{holding.company_name || 'Unknown Company'}</div>
                <div className="sp-holding-details">
                  <div className="sp-holding-detail">
                    <span className="sp-detail-label">Quantity:</span>
                    <span className="sp-detail-value">{holding.total_quantity.toLocaleString()}</span>
                  </div>
                  <div className="sp-holding-detail">
                    <span className="sp-detail-label">Avg Cost:</span>
                    <span className="sp-detail-value">{stockPortfolioService.formatCurrency(holding.average_cost)}</span>
                  </div>
                  <div className="sp-holding-detail">
                    <span className="sp-detail-label">Current:</span>
                    <span className="sp-detail-value">
                      {holding.current_price ? stockPortfolioService.formatCurrency(holding.current_price) : 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="sp-holding-footer">
                  <span className="sp-footer-label">Value:</span>
                  <span className="sp-footer-value">{stockPortfolioService.formatCurrency(holding.current_value)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTransactionsTab = () => {
    if (loading) {
      return (
        <div className="sp-loading-state">
          <div className="sp-loading-spinner"></div>
          <p>Loading transactions...</p>
        </div>
      );
    }

    if (transactions.length === 0) {
      return (
        <div className="sp-empty-state">
          <p>No transactions found</p>
          <button onClick={() => setActiveTab('add')} className="sp-add-first-btn">
            Add your first transaction
          </button>
        </div>
      );
    }

    // Group transactions by date
    const groupedTransactions = transactions.reduce((groups, transaction) => {
      const date = new Date(transaction.transaction_date).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transaction);
      return groups;
    }, {} as Record<string, typeof transactions>);

    // Sort dates (newest first) - assuming transaction_date works for sorting, otherwise parsing might be needed if format varies
    // But since we rely on the API returning sorted or date objects, let's relly on Object.keys behavior or better explicit sort.
    // Actually best to sort the dates based on the timestamp of the first item in the group (since they are identical for the group ideally or we use the string key)
    const sortedDates = Object.keys(groupedTransactions).sort((a, b) => {
      // Parse back to compare
      const dateA = new Date(groupedTransactions[a][0].transaction_date).getTime();
      const dateB = new Date(groupedTransactions[b][0].transaction_date).getTime();
      return dateB - dateA;
    });

    return (
      <div className="sp-transactions-list">
        {sortedDates.map((date) => (
          <div key={date} className="sp-transactions-date-group">
            <div className="sp-date-header">{date}</div>
            <div className="sp-date-group-items">
              {groupedTransactions[date].map((transaction) => (
                <div
                  key={transaction.id}
                  className="sp-transaction-item compact"
                  onClick={() => navigate(`/finance/stocks/company/${transaction.stock_code}`)}
                >
                  <div className="sp-transaction-left">
                    <span className="sp-transaction-code">{transaction.stock_code}</span>
                    <span className={`sp-transaction-badge ${transaction.transaction_type}`}>
                      {transaction.transaction_type}
                    </span>
                  </div>

                  <div className="sp-transaction-math">
                    {transaction.quantity.toLocaleString()} × {stockPortfolioService.formatCurrency(transaction.price_per_share)}
                  </div>

                  <div className="sp-transaction-value">
                    {stockPortfolioService.formatCurrency(transaction.quantity * transaction.price_per_share)}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTransaction(transaction.id);
                    }}
                    className="sp-delete-icon-btn"
                    title="Delete transaction"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCompaniesTab = () => {
    if (loading) {
      return (
        <div className="sp-loading-state">
          <div className="sp-loading-spinner"></div>
          <p>Loading companies...</p>
        </div>
      );
    }

    if (companies.length === 0) {
      return (
        <div className="sp-empty-state">
          <p>No companies found</p>
          <button onClick={() => { setAddType('company'); setActiveTab('add'); }} className="sp-add-first-btn">
            Add your first company
          </button>
        </div>
      );
    }

    return (
      <div className="sp-companies-list">
        {companies.map((company) => (
          <div
            key={company.id}
            className="sp-company-item"
            onClick={() => navigate(`/finance/stocks/company/${company.stock_code}`)}
          >
            <div className="sp-company-code">{company.stock_code}</div>
            <div className="sp-company-name">{company.company_name}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderDividendsTab = () => {
    if (loading) {
      return (
        <div className="sp-loading-state">
          <div className="sp-loading-spinner"></div>
          <p>Loading dividends...</p>
        </div>
      );
    }

    if (dividends.length === 0) {
      return (
        <div className="sp-empty-state">
          <p>No dividends found</p>
          <button onClick={() => { setAddType('dividend'); setActiveTab('add'); }} className="sp-add-first-btn">
            Add your first dividend
          </button>
        </div>
      );
    }

    // Group dividends by date
    const groupedDividends = dividends.reduce((groups, dividend) => {
      const date = new Date(dividend.payment_date).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(dividend);
      return groups;
    }, {} as Record<string, typeof dividends>);

    // Sort dates (newest first)
    const sortedDates = Object.keys(groupedDividends).sort((a, b) => {
      const dateA = new Date(groupedDividends[a][0].payment_date).getTime();
      const dateB = new Date(groupedDividends[b][0].payment_date).getTime();
      return dateB - dateA;
    });

    return (
      <div className="sp-dividends-list">
        {sortedDates.map((date) => (
          <div key={date} className="sp-dividends-date-group">
            <div className="sp-date-header">{date}</div>
            <div className="sp-date-group-items">
              {groupedDividends[date].map((dividend) => (
                <div
                  key={dividend.id}
                  className="sp-dividend-item compact"
                  onClick={() => navigate(`/finance/stocks/company/${dividend.stock_code}`)}
                >
                  <div className="sp-dividend-left">
                    <span className="sp-dividend-code">{dividend.stock_code}</span>
                    <span className={`sp-dividend-badge ${dividend.dividend_type}`}>
                      {dividend.dividend_type}
                    </span>
                  </div>

                  <div className="sp-dividend-info">
                    {dividend.dividend_type === 'cash' ? (
                      `${dividend.shares_held?.toLocaleString()} × ${stockPortfolioService.formatCurrency(dividend.cash_amount || 0)}`
                    ) : (
                      `${dividend.stock_ratio} (${dividend.shares_received?.toLocaleString()} shares)`
                    )}
                  </div>

                  <div className="sp-dividend-value">
                    {dividend.dividend_type === 'cash'
                      ? stockPortfolioService.formatCurrency(dividend.total_cash_received || 0)
                      : `+${dividend.shares_received?.toLocaleString()}`
                    }
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteDividend(dividend.id);
                    }}
                    className="sp-delete-icon-btn"
                    title="Delete dividend"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAddTab = () => {
    return (
      <div className="sp-add-content">
        <div className="sp-add-type-selector">
          <button
            className={`sp-type-btn ${addType === 'transaction' ? 'active' : ''}`}
            onClick={() => setAddType('transaction')}
          >
            Add Transaction
          </button>
          <button
            className={`sp-type-btn ${addType === 'dividend' ? 'active' : ''}`}
            onClick={() => setAddType('dividend')}
          >
            Add Dividend
          </button>
          <button
            className={`sp-type-btn ${addType === 'company' ? 'active' : ''}`}
            onClick={() => setAddType('company')}
          >
            Add Company
          </button>
        </div>

        {addType === 'transaction' ? (
          <div className="sp-add-form">
            <div className="sp-form-row">
              <div className="sp-autocomplete-container">
                <input
                  type="text"
                  placeholder="Type stock code or company name..."
                  value={transactionSearchQuery}
                  onChange={(e) => {
                    setTransactionSearchQuery(e.target.value);
                    setShowTransactionDropdown(true);
                    if (!e.target.value.trim()) {
                      setNewTransaction({ ...newTransaction, stock_code: '' });
                    }
                  }}
                  onFocus={() => setShowTransactionDropdown(true)}
                  className="sp-stock-search"
                />
                {showTransactionDropdown && transactionSearchQuery && (
                  <div className="sp-autocomplete-dropdown">
                    {getFilteredCompanies(transactionSearchQuery).length > 0 ? (
                      getFilteredCompanies(transactionSearchQuery).map(company => (
                        <div
                          key={company.id}
                          className="sp-autocomplete-item"
                          onClick={() => handleTransactionStockSelect(company.stock_code, company.company_name)}
                        >
                          <span className="sp-autocomplete-code">{company.stock_code}</span>
                          <span className="sp-autocomplete-name">{company.company_name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="sp-autocomplete-empty">No matching companies found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="sp-form-row">
              <select
                value={newTransaction.transaction_type}
                onChange={(e) => setNewTransaction({ ...newTransaction, transaction_type: e.target.value as 'buy' | 'sell' })}
                className="sp-type-select"
              >
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
              <input
                type="number"
                placeholder="Quantity"
                value={newTransaction.quantity || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, quantity: parseInt(e.target.value) || 0 })}
                className="sp-quantity-input"
                min="1"
                step="1"
              />
            </div>

            <div className="sp-form-row">
              <input
                type="number"
                placeholder="Price per share"
                value={newTransaction.price_per_share || ''}
                onChange={(e) => setNewTransaction({ ...newTransaction, price_per_share: parseFloat(e.target.value) || 0 })}
                className="sp-price-input"
                min="0"
                step="0.01"
              />
              <input
                type="date"
                value={newTransaction.transaction_date}
                onChange={(e) => setNewTransaction({ ...newTransaction, transaction_date: e.target.value })}
                className="sp-date-input"
              />
            </div>

            <textarea
              placeholder="Notes (optional)..."
              value={newTransaction.notes}
              onChange={(e) => setNewTransaction({ ...newTransaction, notes: e.target.value })}
              className="sp-notes-input"
              rows={2}
            />

            <div className="sp-form-actions">
              <button
                onClick={handleAddTransaction}
                className="sp-save-btn"
                disabled={!newTransaction.stock_code || newTransaction.quantity <= 0 || newTransaction.price_per_share <= 0}
              >
                Save Transaction
              </button>
              <button onClick={() => {
                setNewTransaction({
                  stock_code: '',
                  transaction_type: 'buy',
                  quantity: 0,
                  price_per_share: 0,
                  transaction_date: stockPortfolioService.getCurrentDate(),
                  notes: ''
                });
                setTransactionSearchQuery('');
                setShowTransactionDropdown(false);
              }} className="sp-cancel-btn">
                Clear Form
              </button>
            </div>
          </div>
        ) : addType === 'dividend' ? (
          <div className="sp-add-form">
            <div className="sp-form-row">
              <div className="sp-autocomplete-container">
                <input
                  type="text"
                  placeholder="Type stock code or company name..."
                  value={dividendSearchQuery}
                  onChange={(e) => {
                    setDividendSearchQuery(e.target.value);
                    setShowDividendDropdown(true);
                    if (!e.target.value.trim()) {
                      setNewDividend({ ...newDividend, stock_code: '' });
                    }
                  }}
                  onFocus={() => setShowDividendDropdown(true)}
                  className="sp-stock-search"
                />
                {showDividendDropdown && dividendSearchQuery && (
                  <div className="sp-autocomplete-dropdown">
                    {getFilteredCompanies(dividendSearchQuery).length > 0 ? (
                      getFilteredCompanies(dividendSearchQuery).map(company => (
                        <div
                          key={company.id}
                          className="sp-autocomplete-item"
                          onClick={() => handleDividendStockSelect(company.stock_code, company.company_name)}
                        >
                          <span className="sp-autocomplete-code">{company.stock_code}</span>
                          <span className="sp-autocomplete-name">{company.company_name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="sp-autocomplete-empty">No matching companies found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="sp-form-row sp-dividend-type-row">
              <label className="sp-radio-label">
                <input
                  type="radio"
                  value="cash"
                  checked={newDividend.dividend_type === 'cash'}
                  onChange={(e) => setNewDividend({ ...newDividend, dividend_type: 'cash' })}
                />
                <span>Cash Dividend (Cổ tức tiền mặt)</span>
              </label>
              <label className="sp-radio-label">
                <input
                  type="radio"
                  value="stock"
                  checked={newDividend.dividend_type === 'stock'}
                  onChange={(e) => setNewDividend({ ...newDividend, dividend_type: 'stock' })}
                />
                <span>Stock Dividend (Cổ tức cổ phiếu)</span>
              </label>
            </div>

            {newDividend.dividend_type === 'cash' ? (
              <>
                <div className="sp-form-row">
                  <input
                    type="number"
                    placeholder="Total cash received (VND)"
                    value={newDividend.total_cash_received || ''}
                    onChange={(e) => setNewDividend({ ...newDividend, total_cash_received: parseFloat(e.target.value) || undefined })}
                    className="sp-price-input"
                    min="0"
                    step="0.01"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="sp-form-row">
                  <input
                    type="number"
                    placeholder="Shares received"
                    value={newDividend.shares_received || ''}
                    onChange={(e) => setNewDividend({ ...newDividend, shares_received: parseInt(e.target.value) || undefined })}
                    className="sp-quantity-input"
                    min="1"
                    step="1"
                  />
                </div>
              </>
            )}

            <div className="sp-form-row">
              <input
                type="date"
                value={newDividend.payment_date}
                onChange={(e) => setNewDividend({ ...newDividend, payment_date: e.target.value })}
                className="sp-date-input"
                placeholder="Payment date *"
              />
            </div>


            <textarea
              placeholder="Notes (optional)..."
              value={newDividend.notes}
              onChange={(e) => setNewDividend({ ...newDividend, notes: e.target.value })}
              className="sp-notes-input"
              rows={2}
            />

            <div className="sp-form-actions">
              <button
                onClick={handleAddDividend}
                className="sp-save-btn"
                disabled={
                  !newDividend.stock_code ||
                  (newDividend.dividend_type === 'cash' && !newDividend.total_cash_received) ||
                  (newDividend.dividend_type === 'stock' && !newDividend.shares_received)
                }
              >
                Save Dividend
              </button>
              <button onClick={() => {
                setNewDividend({
                  stock_code: '',
                  dividend_type: 'cash',
                  payment_date: stockPortfolioService.getCurrentDate(),
                  shares_held: undefined,
                  notes: ''
                });
                setDividendSearchQuery('');
                setShowDividendDropdown(false);
              }} className="sp-cancel-btn">
                Clear Form
              </button>
            </div>
          </div>
        ) : (
          <div className="sp-add-form">
            <input
              type="text"
              placeholder="Stock code (e.g., VNM, AAPL)..."
              value={newCompany.stock_code}
              onChange={(e) => setNewCompany({ ...newCompany, stock_code: e.target.value.toUpperCase() })}
              className="sp-code-input"
            />

            <input
              type="text"
              placeholder="Company name..."
              value={newCompany.company_name}
              onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })}
              className="sp-name-input"
            />

            <div className="sp-form-actions">
              <button
                onClick={handleAddCompany}
                className="sp-save-btn"
                disabled={!newCompany.stock_code.trim() || !newCompany.company_name.trim()}
              >
                Save Company
              </button>
              <button onClick={() => {
                setNewCompany({
                  stock_code: '',
                  company_name: ''
                });
              }} className="sp-cancel-btn">
                Clear Form
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCashTab = () => {
    if (loading) {
      return (
        <div className="sp-loading-state">
          <div className="sp-loading-spinner"></div>
          <p>Loading cash data...</p>
        </div>
      );
    }

    return (
      <div className="sp-cash-content">
        <div className="sp-cash-balance-card">
          <div className="sp-cash-balance-label">Current Cash Balance</div>
          <div className="sp-cash-balance-value">{stockPortfolioService.formatCurrency(cashBalance)}</div>
        </div>

        <div className="sp-cash-forms">
          <div className="sp-cash-form">
            <h3>Add / Deduct Cash</h3>
            <div className="sp-form-group">
              <label>Amount</label>
              <input
                type="number"
                value={newCashEntry.amount || ''}
                onChange={(e) => setNewCashEntry({ ...newCashEntry, amount: parseFloat(e.target.value) })}
                placeholder="0"
              />
            </div>
            <div className="sp-form-group">
              <label>Date</label>
              <input
                type="date"
                value={newCashEntry.transaction_date}
                onChange={(e) => setNewCashEntry({ ...newCashEntry, transaction_date: e.target.value })}
              />
            </div>
            <div className="sp-form-group">
              <label>Notes</label>
              <textarea
                value={newCashEntry.notes}
                onChange={(e) => setNewCashEntry({ ...newCashEntry, notes: e.target.value })}
                placeholder="Topup source, deduction reason, etc."
              />
            </div>
            <div className="sp-cash-form-actions">
              <button onClick={() => handleAddCash('topup')} className="sp-topup-btn">Topup Cash</button>
              <button onClick={() => handleAddCash('deduct')} className="sp-deduct-btn">Deduct Cash</button>
            </div>
          </div>
        </div>

        <div className="sp-cash-history">
          <h3>Cash Transactions</h3>
          {cashTransactions.length === 0 ? (
            <p className="sp-no-data">No cash transactions yet</p>
          ) : (
            <div className="sp-cash-transactions-list">
              {cashTransactions.map((tx) => (
                <div key={tx.id} className="sp-cash-tx-item">
                  <div className="sp-cash-tx-header">
                    <span className={`sp-cash-tx-type ${tx.transaction_type}`}>
                      {tx.transaction_type.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className="sp-cash-tx-date">{new Date(tx.transaction_date).toLocaleDateString()}</span>
                  </div>
                  <div className="sp-cash-tx-body">
                    <span className={`sp-cash-tx-amount ${Number(tx.amount) >= 0 ? 'positive' : 'negative'}`}>
                      {Number(tx.amount) >= 0 ? '+' : ''}{stockPortfolioService.formatCurrency(tx.amount)}
                    </span>
                    <span className="sp-cash-tx-balance">
                      Balance: {stockPortfolioService.formatCurrency(tx.balance_after)}
                    </span>
                  </div>
                  {tx.notes && <div className="sp-cash-tx-notes">{tx.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="stock-portfolio">
        <div className="sp-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>📈 Stock Portfolio</h1>
        </div>
        <div className="sp-auth-required">
          <p>Please sign in to manage your stock portfolio</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stock-portfolio">
      <div className="sp-header">
        <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
        <h1>📈 Stock Portfolio</h1>
        <button
          className="sp-add-btn"
          onClick={() => setActiveTab('add')}
          title="Add transaction or company"
        >
          ➕
        </button>
      </div>

      {error && (
        <div className="sp-error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="sp-main-content">
        {activeTab === 'portfolio' && renderPortfolioTab()}
        {activeTab === 'transactions' && renderTransactionsTab()}
        {activeTab === 'companies' && renderCompaniesTab()}
        {activeTab === 'dividends' && renderDividendsTab()}
        {activeTab === 'cash' && renderCashTab()}
        {activeTab === 'add' && renderAddTab()}
      </div>

      {/* Bottom Tab Navigation */}
      <div className="sp-bottom-tabs">
        <button
          className={`sp-tab-btn ${activeTab === 'portfolio' ? 'active' : ''}`}
          onClick={() => setActiveTab('portfolio')}
        >
          <span className="sp-tab-icon">💼</span>
          <span className="sp-tab-label">Portfolio</span>
        </button>
        <button
          className={`sp-tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          <span className="sp-tab-icon">📊</span>
          <span className="sp-tab-label">Transactions</span>
        </button>
        <button
          className={`sp-tab-btn ${activeTab === 'dividends' ? 'active' : ''}`}
          onClick={() => setActiveTab('dividends')}
        >
          <span className="sp-tab-icon">💰</span>
          <span className="sp-tab-label">Dividends</span>
        </button>
        <button
          className={`sp-tab-btn ${activeTab === 'companies' ? 'active' : ''}`}
          onClick={() => setActiveTab('companies')}
        >
          <span className="sp-tab-icon">🏢</span>
          <span className="sp-tab-label">Companies</span>
        </button>
        <button
          className={`sp-tab-btn ${activeTab === 'cash' ? 'active' : ''}`}
          onClick={() => setActiveTab('cash')}
        >
          <span className="sp-tab-icon">💵</span>
          <span className="sp-tab-label">Cash</span>
        </button>
      </div>
    </div>
  );
};

export default StockPortfolio;

